import { useMemo } from 'react';
import { PencilIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Button } from '@rocco/components/button';
import { Separator } from '@rocco/components/separator';
import type { MeUser } from '~/server/types';
import type { Conversation } from '../../types';
import { useChatBoxes } from '../../hooks/use-chat-boxes';
import { DRAWER_WIDTH_REM } from '../../utils';
import { ChatInboxDrawerHeader } from './ChatInboxDrawerHeader';
import { ChatInboxSwitcher } from './ChatInboxSwitcher';
import { ChatInboxList } from './ChatInboxList';

/**
 * The floating chat inbox drawer. This shows the list of conversations for the
 * current logged-in user. If the user has inbox access to one or more professionals,
 * they can switch between their personal inbox and the company inboxes.
 * @see {@link ChatBoxes}
 */
export function ChatInboxDrawer({
    className,
    style,
    conversations,
    user,
    professionalID,
    isLoading = false,
    hasMore = false,
    onInboxChange,
    onFiltersChange,
    onLoadMore,
}: {
    className?: string;
    style?: React.CSSProperties;
    /**
     * The user's list of conversations. A `null` value means that the conversations
     * are not yet fetched.
     */
    conversations: Conversation[] | null;
    /**
     * The logged-in user.
     */
    user: MeUser;
    /**
     * The professional ID of the company inbox to display. If `null`, the
     * personal inbox will be displayed.
     */
    professionalID: number | null;
    /**
     * The conversations are being loaded.
     */
    isLoading?: boolean;
    /**
     * There are more conversations to load.
     */
    hasMore?: boolean;
    /**
     * Callback called when the selected inbox changes.
     */
    onInboxChange?: (professionalID: number | null) => void;
    /**
     * Callback called when the filters are changed.
     */
    onFiltersChange?: (filters: {
        search: string;
        isArchived: boolean;
        projectIDs: string[];
    }) => void;
    /**
     * Load more conversations by fetching the next page.
     */
    onLoadMore?: () => void;
}) {
    const { openChatBox } = useChatBoxes();
    const companyInboxes = useMemo(() => {
        return user.Professionals.filter(
            (professional) => professional.HasInboxAccess,
        );
    }, [user.Professionals]);

    return (
        <aside
            className={cn(
                'flex h-full flex-col rounded-t-xl bg-white text-black shadow-chat',
                className,
            )}
            style={{ width: `${DRAWER_WIDTH_REM}rem`, ...style }}
        >
            {/* Header */}
            <ChatInboxDrawerHeader
                user={user}
                professionalID={professionalID}
            />
            <Separator />
            {(!!professionalID || companyInboxes.length > 0) && (
                <ChatInboxSwitcher
                    className="px-3 pt-3"
                    user={user}
                    professionalID={professionalID}
                    onInboxChange={onInboxChange}
                />
            )}
            {/* Inbox list */}
            <ChatInboxList
                className="min-h-0 flex-1 px-3 pt-3"
                conversations={conversations ?? []}
                user={user}
                professionalID={professionalID}
                isLoading={!conversations || isLoading}
                hasMore={hasMore}
                onFiltersChange={onFiltersChange}
                onLoadMore={onLoadMore}
            />
            <Separator />
            {/* New message button */}
            <footer className="flex items-center justify-center px-2 py-8">
                {/* Make sure the new message button lines up with the chat box send button. */}
                <Button
                    className="flex items-center gap-2 bg-black/70 px-16 py-2 leading-tight hover:bg-black"
                    color="dark"
                    size="larger"
                    onClick={() => openChatBox(0, null)}
                >
                    New Message
                    <PencilIcon className="size-4" />
                </Button>
            </footer>
        </aside>
    );
}
